import React, { useCallback, useEffect, useRef, useState } from 'react';
import RxntService from '../../services/RxntService';
import { OK } from '../../utils/constant';
import { mapGatewayPatientToSdk } from '../../utils/rxntLaunchUtils';
import { defaultRxntLaunchReturnUrl, loadRxntLaunchSdk } from '../../utils/rxntLaunchSdk';

/** SDK POSTs to `{baseUrl}/ehr-gateway/launch` — host only, not the API path prefix. */
const RXNT_LAUNCH_BASE_URL =
  process.env.REACT_APP_RXNT_LAUNCH_BASE_URL || 'https://app2.rxnt.com';

const DEFAULT_BUTTON_CLASS = 'btn btn-primary sh_btnprimary btn-sm';

const HIDDEN_SDK_HOST_STYLE = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/**
 * RXNT EHR launch via official Integration SDK (<rxnt-launch-button>).
 *
 * UI: Snap-styled <button>; the SDK custom element is visually hidden and clicked programmatically
 * (SDK default styling does not match sh_btnprimary).
 *
 * Security: RXNT client_id/secret never touch the browser — authFactory uses GET /rxnt/launch/token.
 *
 * @param {string} [text] Button label
 * @param {string} [providerId] Snap provider_id (maps to RXNT doctor when rxntDoctorId omitted)
 * @param {number|null} [rxntDoctorId] EHR Gateway doctorId (targetDoctorId)
 * @param {string|null} [employeeId] Snap employee — API builds full patientData
 * @param {number|null} [rxntPatientId] RXNT patient id when no employee_id
 * @param {object|null} [gatewayPatient] Worklist rx.patient — merged client-side if API has no patientData
 * @param {string} [returnUrl] Absolute URL RXNT returns to after session (required by launch API)
 * @param {boolean} [disabled] Disables visible button (e.g. missing patient on notification row)
 * @param {string} [disabledTitle] Tooltip when disabled
 * @param {function} [onError] (message: string) => void
 * @param {function} [onLaunchSuccess] () => void
 */
const RxntLaunchButton = ({
  text = 'Open RXNT',
  providerId = '',
  rxntDoctorId = null,
  employeeId = null,
  rxntPatientId = null,
  gatewayPatient = null,
  returnUrl,
  disabled = false,
  disabledTitle = 'Select a mapped provider first',
  className = '',
  buttonClassName = DEFAULT_BUTTON_CLASS,
  onError,
  onLaunchSuccess,
}) => {
  const hostRef = useRef(null);
  const buttonRef = useRef(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState(null);

  const canLaunch =
    sdkReady && !disabled && providerId && rxntDoctorId != null && rxntDoctorId !== '';

  useEffect(() => {
    let cancelled = false;

    loadRxntLaunchSdk()
      .then(() => {
        if (!cancelled) {
          setSdkReady(true);
          setSdkError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setSdkError(err?.message || 'Failed to load RXNT SDK');
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !sdkReady || sdkError) {
      return undefined;
    }

    let button = buttonRef.current;
    if (!button) {
      // One hidden web component per React instance; configure() is re-run when launch params change.
      button = document.createElement('rxnt-launch-button');
      button.setAttribute('text', text);
      host.appendChild(button);
      buttonRef.current = button;
    }

    const launchReturnUrl = returnUrl || defaultRxntLaunchReturnUrl();
    const contextParams = {
      return_url: launchReturnUrl,
    };
    if (providerId) contextParams.provider_id = providerId;
    if (rxntDoctorId != null && rxntDoctorId !== '') {
      contextParams.rxnt_doctor_id = rxntDoctorId;
    }
    if (employeeId) contextParams.employee_id = employeeId;
    if (rxntPatientId != null && rxntPatientId !== '') {
      contextParams.rxnt_patient_id = rxntPatientId;
    }

    button.configure({
      baseUrl: RXNT_LAUNCH_BASE_URL.replace(/\/$/, ''),
      authFactory: async () => {
        const res = await RxntService.getLaunchToken();
        if (res?.code !== OK || !res?.data?.accessToken) {
          throw new Error(res?.message || 'Failed to obtain RXNT access token');
        }
        return res.data.accessToken;
      },
      launchDataFactory: async () => {
        const res = await RxntService.getLaunchContext(contextParams);
        if (res?.code !== OK || !res?.data?.launchData) {
          throw new Error(res?.message || 'Failed to build RXNT launch context');
        }
        const launchData = { ...res.data.launchData };
        // Fallback: gateway-only pending rows may lack employee_id / rxnt_patients sync on API.
        if (!launchData.patientData && gatewayPatient) {
          const chart =
            employeeId || gatewayPatient.chartNumber || gatewayPatient.chartNo || '';
          const mapped = mapGatewayPatientToSdk(gatewayPatient, chart);
          if (mapped) launchData.patientData = mapped;
        }
        return launchData;
      },
      onLaunchSuccess: () => {
        if (onLaunchSuccess) onLaunchSuccess();
      },
      onError: (err) => {
        const message =
          err?.message || (typeof err === 'string' ? err : 'RXNT launch failed');
        if (onError) {
          onError(message);
        } else {
          console.error('RXNT launch error', err);
        }
      },
    });

    return undefined;
  }, [
    sdkReady,
    sdkError,
    text,
    providerId,
    rxntDoctorId,
    employeeId,
    rxntPatientId,
    gatewayPatient,
    returnUrl,
    onError,
    onLaunchSuccess,
  ]);

  useEffect(
    () => () => {
      const host = hostRef.current;
      if (buttonRef.current && host?.contains(buttonRef.current)) {
        host.removeChild(buttonRef.current);
        buttonRef.current = null;
      }
    },
    []
  );

  // SDK exposes no public launch() — delegate to its shadow DOM button.
  const triggerSdkLaunch = useCallback(() => {
    const el = buttonRef.current;
    const inner = el?.shadowRoot?.querySelector('button');
    if (inner) {
      inner.click();
    }
  }, []);

  const wrapClass = ['rxnt-launch-wrap', 'position-relative', 'd-inline-flex', className]
    .filter(Boolean)
    .join(' ');

  if (sdkError) {
    return (
      <button
        type="button"
        className={`btn btn-outline-danger btn-sm ${className}`.trim()}
        title={sdkError}
        disabled
      >
        RXNT unavailable
      </button>
    );
  }

  const showLoading = !sdkReady && providerId && rxntDoctorId != null && rxntDoctorId !== '';
  const isDisabled = disabled || !canLaunch;
  const title = isDisabled
    ? disabled
      ? disabledTitle
      : showLoading
        ? 'Loading RXNT SDK…'
        : disabledTitle
    : 'Launch RXNT EHR for the selected provider';

  return (
    <span className={wrapClass}>
      <span ref={hostRef} style={HIDDEN_SDK_HOST_STYLE} aria-hidden="true" />
      <button
        type="button"
        className={buttonClassName}
        disabled={isDisabled}
        title={title}
        onClick={() => {
          if (canLaunch) triggerSdkLaunch();
        }}
      >
        {showLoading ? 'Loading…' : text}
      </button>
    </span>
  );
};

export default RxntLaunchButton;
